import { Component, type ReactNode } from 'react';
import { captureError } from '../lib/analytics';

/**
 * React error boundary that reports render errors to PostHog error tracking
 * and shows a safe fallback instead of a blank page.
 *
 * Unhandled errors/rejections are already covered by PostHog exception
 * autocapture; this covers React render errors, which never reach
 * `window.onerror` and would otherwise be invisible in PostHog.
 */
export default class PostHogErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: { componentStack?: string }): void {
    captureError(error, {
      component_stack: info.componentStack?.slice(0, 2000) ?? null,
      boundary: 'PostHogErrorBoundary',
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="mx-auto max-w-lg p-8 text-center">
            <h2 className="text-lg font-semibold text-glass-text">Something went wrong</h2>
            <p className="mt-2 text-sm text-glass-text-2">
              The error has been reported. Please refresh the page or go back and try again.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded-md bg-accent2 px-4 py-2 text-sm font-medium text-white"
            >
              Refresh page
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
